import { EPhoneNumberVerificationType, EThirdPartyProvider } from '../util/constants';

export enum EErrorResponseType {
    expiredPassword = 'expired-password',
    migrationRequired = 'migration-required',
    migrationRequiredExistingAccount = 'migration-required-existing-account',
    manualMigrationRequired = 'manual-migration-required',
    subscriberConsumerConflict = 'subscriber-consumer-conflict',
    subscriberConsumerExistingLink = 'subscriber-consumer-existing-link',
    thirdParty = 'external-authentication-failure',
    noCorporateUserFound = 'corporate-user-not-found',
    corporateUserConflict = 'corporate-user-conflict',
    corporateUserNoPermission = 'corporate-user-no-permission',
    invalidCsrfToken = 'invalid-antiforgery-token',
    adminStaffSignin = 'admin-staff-signin',
    accountUnverified = 'account-unverified',
}

export enum EConsentStatus {
    continue = 'continue',
    cancel = 'cancel',
}

export type TCsrfResponse = {
    requestVerificationToken: string;
};

export type TCountriesListResponse = { name: string; code: string }[];

export type TLoginPost = {
    username: string;
    password: string;
    subscriberId?: string;
    isStaff?: boolean;
    customerAccountId?: string;
};

export type TLoginResponse = {
    requireAgreement: boolean;
    redirectUrl: string;
    requireMfa: boolean;
    userId: string;
};

export type TLogoutPost = {
    logoutId: string;
};

export type TLogoutResponse = {
    postLogoutRedirectUri: string;
};

export type TMfaVerifyPost = {
    authenticatorCode: string;
};

export type TMfaRecoveryPost = {
    recoveryCode: string;
};

export type TForgotPasswordPost = {
    email: string;
    subscriberId?: string;
    usePasscode?: boolean;
};

export type TForgotPasswordStaffPost = {
    email: string;
    customerAccountId: string;
    subscriberId: string;
};

export type TVerifyForgotPasswordPost = {
    email: string;
    code: string;
    customerAccountId?: string;
    isStaff?: boolean;
};

export type TVerifyForgotPasswordResponse = {
    code: string;
    userId: string;
};

export type TResetPasswordPost = {
    token: string;
    password: string;
};

export type TResetPasswordStaffPost = {
    token: string;
    password: string;
};

export type TResetPasswordResponse = {};

export type TForgotPasswordResponse = {};

export type TRegister3rdPartyPost = {
    givenName: string;
    surname: string;
    countryCode: string;
    dependentUsers?: TDependentUser[];
};

export type TSetupRegister3rdPartyResponse = {
    countries: { name: string; code: string; phoneCode: string }[];
    subscriberId: number | null;
    givenName: string;
    surname: string;
};

export type TRegister3rdPartyResponse = {
    redirectUrl: string;
};

export type TVerifyAccountResponse = {
    siteBranding?: {
        subscriberId: number;
        subscriberName: string;
        businessUrl: string;
        logoUrl: string;
        mobileLogoUrl: string;
    };
    userBusinesses: {
        users: {
            id: string;
            firstName: string;
            lastName: string;
        }[];
        subscriberId: string;
        subscriberName: string;
        city: string;
        stateOrProvinceCode: string;
        countryCode: string;
    }[];
    legacyManualSyncToken: string;
};

export type TApiErrorResponse = {
    type: string;
    title: string;
    detail: string;
    code: number;
};

//Branding info fetched from Rest API /Subscribers/SiteInformation
export type TSiteInfoResponse = {
    StudioId: number;
    StudioName: string;
    BusinessUrl: string;
    LogoUrl: string;
    MobileLogoUrl: string;
};

//Branding info fetched from Identity Provider /Account/LoginInfo
export type TLoginInfoResponse = {
    subscriberId: number;
    regionId: number | null;
    customerBrandId: string | null;
    customerAccountId: string | null;
    primaryColor: string | null;
    isDarkMode: boolean;
    isDefaultFamilyAccount: boolean;
};

export type TChangeEmailResponse = {
    email: string;
};

export type TChangeEmailStaffResponse = {
    email: string;
};

export type TCreateAccountPost = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    streetAddress?: string;
    locality?: string;
    region?: string;
    countryCode: string;
    subscriberId?: string;
    dependentUsers?: TDependentUser[];
    linkingToken?: string;
    marketingOptIn?: boolean;
    usePasscode?: boolean;
};

export type TCreateAccountResponse = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    countryCode: string;
};

export type TRequestVerifyPost = {
    email?: string;
    userId?: string;
    subscriberId?: string;
    usePasscode?: boolean;
};

export type TRequestVerifyLoginPost = {
    email: string;
    userId: string;
    subscriberId?: string;
};

export type TRequestVerifyStaffPost = {
    email: string;
    customerAccountId: string;
};

export type TRequestVerifyResponse = {};

export type TStartGraduationResponse = {
    firstName: string;
    lastName: string;
    unconfirmedEmail: string;
    countryCode: string;
};

export type TCompleteGraduationPost = {
    code: string;
    firstName: string;
    lastName: string;
    password: string;
    countryCode: string;
};

export type TCompleteGraduationResponse = {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    countryCode: string;
    businesses: {
        id: number;
        name: string;
        locality: string;
        region: string;
        countryCode: string;
    }[];
    legacyManualSyncToken: string;
};

export type TAgreeToTermsPost = {
    returnUrl?: string;
};

export type TAgreeToTermsResponse = {
    redirectUrl: string;
};

export type TStartConfirmAccountResponse = {
    firstName: string;
    lastName: string;
    email: string;
    countryCode: string;
};

export type TCompleteConfirmAccountPost = {
    code: string;
    firstName: string;
    lastName: string;
    password: string;
    countryCode: string;
    unconfirmedLinkId?: string;
    dependentUsers?: {
        firstName: string;
        lastName: string;
        unconfirmedLinkId?: string;
    }[];
};

export type TCompleteConfirmAccountResponse = {
    userBusinesses: [
        {
            users: [
                {
                    id: string;
                    firstName: string;
                    lastName: string;
                },
            ];
            subscriberId: string;
            subscriberName: string;
            city: string;
            stateOrProvinceCode: string;
            countryCode: string;
        },
    ];
    legacyManualSyncToken: string;
};

export type TStartConfirmStaffAccountResponse = {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
};

export type TCompleteConfirmStaffAccountPost = {
    code: string;
    firstName: string;
    lastName: string;
    password: string;
    countryCode: string;
    subscriberId?: number;
};

export type TRequestConfirmStaffAccountPost = {
    customerAccountId: string;
    subscriberId?: number;
};

export type TStartConfirmStaffPhoneNumberPost = {
    phoneNumber: string;
    channel: EPhoneNumberVerificationType;
};

export type TCompleteConfirmStaffPhoneNumberPost = {
    verificationCode: string;
};

export type TMigrateStaffAccountPost = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    linkingToken: string;
};

export type TMigrateStaffAccountResponse = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    customerAccountId: string;
    requiresVerification: boolean;
    createdAtUtc: string;
};

export type TMigrationRequiredError = TApiErrorResponse & {
    firstName: string;
    lastName: string;
    username: string;
    countryCode: string;
    linkingToken: string;
};

export type TMigrationRequiredExistingAccountError = TApiErrorResponse & {
    userId: string;
    username: string;
    custodian: {
        firstName: string;
        lastName: string;
    };
    profileId: number;
    firstName: string;
    lastName: string;
};

export type TSetupConsentResponse = {
    clientName: string;
    clientLogoUrl: string;
    resourceScopes: {
        name: string;
        displayName: string;
        description: string;
    }[];
};

export type TConsentPost = {
    button: EConsentStatus;
    scopesConsented: string[];
    returnUrl: string;
};

export type TConsentResponse = {
    redirectUrl: string;
};

export type TTrackEventPost = {
    event: string;
    properties?: object;
    context?: object;
    accountId?: string;
};

export type TApiErrorResponseWUsername = TApiErrorResponse & {
    username: string;
};

export type TApiErrorResponseWUserId = TApiErrorResponse & {
    userId: string;
};

export type TDependentUser = {
    firstName: string;
    lastName: string;
    linkingToken?: string;
};

export type TFindConsumerPost = {
    username: string;
    subscriberId?: number;
};

export type TFindConsumerResponse = {
    username: string;
    firstName: string;
    lastName: string;
    providers: { providerKey: string; providerName: EThirdPartyProvider }[];
};

export type TVerifyAndCreateDependentParams = {
    userId: string;
    firstName: string;
    lastName: string;
    profileId: string;
    subscriberId: string;
    code: string;
};

export type TVerifyAndCreateDependentResponse = {
    dependents: {
        id: string;
        firstName: string;
        lastName: string;
    }[];
};

export type TAddPasswordPost = {
    code: string;
    password: string;
};
